import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelectTheme } from "../../store";
import {
  DefaultTaskScheduleSettingsTextStyle,
  TaskScheduleSettingsRelativeDayInput,
  TaskScheduleSettingsRemoveExpirationDateButton,
  TaskScheduleSettingsSetExpirationDateButton,
} from "./task_schedule_settings_input";
import { ScheduleFormValidationErrorMessages } from "../../model/schedule";
import { useMount } from "react-use";
import { registerName } from "../../lib/form";
import { isNil } from "lodash";
import { RIF } from "../../lib";

interface TaskScheduleSettingsRecurringDailyRelativeForm {
  startDay: number;
  endDay?: number;
}

export interface TaskScheduleSettingsRecurringDailyRelativeFormProps {
  values: Partial<TaskScheduleSettingsRecurringDailyRelativeForm>;
  onValidated: (form: TaskScheduleSettingsRecurringDailyRelativeForm) => void;
  onError: () => void;
}

export const TaskScheduleSettingsRecurringDailyRelative = (
  props: TaskScheduleSettingsRecurringDailyRelativeFormProps,
) => {
  const { values, onValidated, onError } = props;

  const { color, fontSize, fontWeight } = useSelectTheme();

  const textStyle = DefaultTaskScheduleSettingsTextStyle({ color, fontSize, fontWeight });

  const defaultEndDay = values.endDay ?? 7;
  const [cachedEndDay, setCachedEndDay] = useState(defaultEndDay);

  const {
    register,
    formState: { isValidating, isValid, errors },
    trigger,
    setValue,
    getValues,
  } = useForm<TaskScheduleSettingsRecurringDailyRelativeForm>({
    mode: "onChange",
    defaultValues: {
      startDay: values.startDay ?? 1,
      endDay: values.endDay,
    },
  });

  const [latestErrorMessage, setLatestErrorMessage] = useState<string | null>(null);

  const [showSetExpirationDateButton, setShowSetExpirationDateButton] = useState(
    isNil(values.endDay),
  );

  useEffect(() => {
    for (const error of Object.values(errors)) {
      if (error?.message) {
        setLatestErrorMessage(error.message);
        onError();
        return;
      }
    }
    setLatestErrorMessage(null);
  }, [errors.startDay, errors.endDay]);

  useMount(() => {
    trigger();
  });

  useEffect(() => {
    if (isValid && !isValidating) {
      const form = getValues();
      onValidated(form);
    }
  }, [isValid, isValidating]);

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <span css={{ ...textStyle }}>The task will appear every day from</span>
        <TaskScheduleSettingsRelativeDayInput
          register={registerName(register, "startDay")}
          failedValidation={!!errors.startDay || !!errors.endDay}
          {...{
            required: {
              value: true,
              message: ScheduleFormValidationErrorMessages.required("Start Day"),
            },
            min: {
              value: 1,
              message: ScheduleFormValidationErrorMessages.shouldBePositiveNumber("Start Day"),
            },
            validate: {
              lessThanOrEqual: (value) => {
                const endDay = getValues("endDay");
                if (value && endDay && value > endDay) {
                  return ScheduleFormValidationErrorMessages.shouldBeLessThanOrEqual(
                    "Start Day",
                    "End Day",
                  );
                }
                return true;
              },
            },
            deps: ["endDay"],
          }}
        />
        {RIF(
          showSetExpirationDateButton,
          <>
            <span css={{ ...textStyle }}>and continue indefinitely.</span>
            <TaskScheduleSettingsSetExpirationDateButton
              onClick={() => {
                setValue("endDay", cachedEndDay);
                setShowSetExpirationDateButton(false);
                trigger();
              }}
            />
          </>,
        )}
        {RIF(
          !showSetExpirationDateButton,
          <>
            <span css={{ ...textStyle }}>to</span>
            <TaskScheduleSettingsRelativeDayInput
              register={registerName(register, "endDay")}
              failedValidation={!!errors.endDay || !!errors.startDay}
              {...{
                min: {
                  value: 1,
                  message: ScheduleFormValidationErrorMessages.shouldBePositiveNumber("End Day"),
                },
                validate: {
                  greaterThanOrEqual: (value) => {
                    const startDay = getValues("startDay");
                    if (value && startDay && value < startDay) {
                      return ScheduleFormValidationErrorMessages.shouldBeGreaterThanOrEqual(
                        "End Day",
                        "Start Day",
                      );
                    }
                    return true;
                  },
                },
                deps: ["startDay"],
              }}
            />
            <TaskScheduleSettingsRemoveExpirationDateButton
              onClick={() => {
                setCachedEndDay(getValues("endDay") ?? defaultEndDay);
                setValue("endDay", undefined);
                setShowSetExpirationDateButton(true);
                trigger(["endDay", "startDay"]);
              }}
            />
          </>,
        )}
      </div>
      {RIF(latestErrorMessage, (message) => (
        <div css={{ ...textStyle, color: color.warning }}>{message}</div>
      ))}
    </div>
  );
};
